// src/hooks/useCapsuleDetails.js
import { useState, useEffect, useCallback, useRef } from "react";
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "../firebase";
import { fetchUsersBasicInfoByIds } from "../services/profileService";
import { recordOpenedCapsuleHistory } from "../services/historyService";
import { queuePendingSharedCapsuleClaim } from "../services/sharedCapsuleClaimService";
import { getDeviceId } from "../utils/deviceId";
import {
	applyRepairedCapsuleMedia,
	getSeededPlaceMediaRepairUrls,
	normalizeSeededPlaceCapsuleMedia,
	shouldHydrateSeededPlaceMediaOnOpen,
	shouldAttemptSeededPlaceMediaRepair,
	withholdRepairableSeededPlaceMedia,
} from "../utils/seededPlaceMedia";
import { getLegacyFindCapsuleTextUpgrade } from "../utils/findQuickTake";
import { isCapsuleEnded } from "../utils/capsuleAvailability";

// Thrown when a scheduled capsule is opened after its endDate. Carries a code
// so the detail page can say "this has ended" instead of "not found".
function createCapsuleEndedError() {
	const error = new Error("This time capsule has ended.");
	error.code = "capsule-ended";
	return error;
}

function collectCapsuleParticipantUserIds(capsule) {
	const ids = new Set();
	const add = (value) => {
		const id = String(value || "").trim();
		if (id) ids.add(id);
	};
	const addList = (values) => {
		if (!Array.isArray(values)) return;
		values.forEach(add);
	};

	add(capsule?.senderId);
	addList(capsule?.recipientUserIds);
	addList(capsule?.claimedBy);

	if (Array.isArray(capsule?.comments)) {
		capsule.comments.forEach((comment) => {
			add(comment?.authorId || comment?.userId || comment?.uid);
		});
	}

	return Array.from(ids);
}

// Mirrors canGenerateCapsuleBody on the server. Kept deliberately narrow so a
// non-discover capsule never triggers a billable call.
function shouldGenerateCapsuleBody(capsule) {
	if (capsule?.seeded !== true) return false;
	if (String(capsule?.seedType || "").trim().toLowerCase() !== "discover") return false;
	return String(capsule?.messageBodyGeneratedBy || "").trim() !== "gemini_maps_grounding";
}

export function useCapsuleDetails(id, shareToken = "") {
	const uid = auth.currentUser?.uid;
	const [capsule, setCapsule] = useState(null);
	const [senderUser, setSenderUser] = useState(null);
	const [usersList, setUsersList] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadingSender, setLoadingSender] = useState(false);
	const [error, setError] = useState(null);
	const [loadPhase, setLoadPhase] = useState("opening");
	const [claimStatus, setClaimStatus] = useState("idle");
	const [claimError, setClaimError] = useState(null);
	const [sharedAccessLevel, setSharedAccessLevel] = useState("full");
	const [isGeneratingBody, setIsGeneratingBody] = useState(false);
	const repairStartedRef = useRef(new Set());
	const bodyGenerationStartedRef = useRef(new Set());
	const capsuleRef = useRef(null);
	// Component-lifetime mount flag. Body generation must survive a `capsule`
	// identity change mid-flight (seeded place media hydration re-sets it on
	// essentially every fresh discover capsule), so only an unmount may suppress
	// the swap and the shimmer reset.
	const isMountedRef = useRef(true);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		capsuleRef.current = capsule;
	}, [capsule]);

	const repairSeededPlaceMedia = useCallback(async (mediaItem = null, capsuleOverride = null) => {
		if (!id) return false;

		const capsuleToRepair = capsuleOverride || capsuleRef.current;
		const shouldHydrateOnOpen = !mediaItem && shouldHydrateSeededPlaceMediaOnOpen(capsuleToRepair);
		if (mediaItem && !shouldAttemptSeededPlaceMediaRepair(capsuleToRepair, mediaItem)) return false;

		const storedPhotoUrls = getSeededPlaceMediaRepairUrls(capsuleToRepair);
		if (!storedPhotoUrls.length && !shouldHydrateOnOpen) return false;
		if (repairStartedRef.current.has(id)) return false;
		repairStartedRef.current.add(id);

		try {
			const repairFn = httpsCallable(functions, "repairSeededCapsulePlaceMedia");
			const result = await repairFn({
				capsuleId: id,
				photoUrls: storedPhotoUrls,
				hydrateAll: shouldHydrateOnOpen,
			});
			const nextMedia = Array.isArray(result?.data?.media) ? result.data.media : [];
			if (!nextMedia.length) return false;
			setCapsule((prev) => ({
				...applyRepairedCapsuleMedia(prev, nextMedia),
				...(result?.data?.placeFacts ? { placeFacts: result.data.placeFacts } : {}),
				...(result?.data?.placePhotoHydrationStatus
					? { placePhotoHydrationStatus: result.data.placePhotoHydrationStatus }
					: {}),
			}));
			return true;
		} catch (err) {
			console.warn("Failed to repair seeded place media:", err);
			repairStartedRef.current.delete(id);
			return false;
		}
	}, [id]);

	useEffect(() => {
		if (!id) return;

		const load = async () => {
			setLoading(true);
			setError(null);
			setClaimError(null);
			setLoadPhase(shareToken ? "verifying" : "opening");
			setSharedAccessLevel(shareToken ? "loading" : "full");
			try {
				if (shareToken) {
					queuePendingSharedCapsuleClaim({
						capsuleId: id,
						token: shareToken,
						path: `/capsule/${id}?token=${encodeURIComponent(shareToken)}`,
					});
				}

				let data = null;
				let nextSharedAccessLevel = "full";
				// 1) Fetch the capsule. Shared-token validation is the critical path.
				const ref = doc(db, "timeCapsules", id);
				if (shareToken) {
					const getSharedCapsuleFn = httpsCallable(functions, "getSharedCapsuleByToken");
					// Durable device id → anon dedup key for locked-preview impressions.
					const anonId = await getDeviceId().catch(() => "");
					const sharedResult = await getSharedCapsuleFn({ capsuleId: id, token: shareToken, anonId });
					const sharedPayload = sharedResult?.data || {};
					const sharedData = sharedPayload?.capsule || null;
					if (!sharedData) throw new Error("Time capsule not found");
					nextSharedAccessLevel =
						sharedPayload?.accessLevel === "preview" || sharedData?.previewLocked === true
							? "preview"
							: "full";
					data = { id, ...sharedData };
					setSharedAccessLevel(nextSharedAccessLevel);
					setLoadPhase("opening");
				} else {
					const snap = await getDoc(ref);
					if (!snap.exists()) throw new Error("Time capsule not found");
					data = { id: snap.id, ...snap.data() };
					setSharedAccessLevel("full");
					setLoadPhase("opening");
				}
				// A scheduled capsule (sponsored / memberExclusive) disappears the
				// moment its endDate passes, and the share link has to stop resolving
				// with it — not just the map markers. Checked before the capsule is
				// put on screen and before any read/claim/history side effects, so an
				// ended capsule never leaks its media, body or CTA.
				if (isCapsuleEnded(data)) {
					throw createCapsuleEndedError();
				}

				const normalizedCapsule = normalizeSeededPlaceCapsuleMedia(data);
				const textUpgrade = getLegacyFindCapsuleTextUpgrade(normalizedCapsule);
				const displayCapsule = textUpgrade
					? { ...normalizedCapsule, ...textUpgrade }
					: normalizedCapsule;
				const repairUrls = getSeededPlaceMediaRepairUrls(normalizedCapsule);
				const shouldHydrateOnOpen = shouldHydrateSeededPlaceMediaOnOpen(normalizedCapsule);
				setCapsule(
					repairUrls.length
						? withholdRepairableSeededPlaceMedia(displayCapsule)
						: displayCapsule
				);
				setLoading(false);
				setLoadPhase("ready");
				if (nextSharedAccessLevel !== "preview" && textUpgrade) {
					const upgradeFn = httpsCallable(functions, "legacyFind_upgradeCapsuleText");
					upgradeFn({ capsuleId: id }).catch((upgradeErr) => {
						console.warn("Failed to upgrade legacy Find capsule text:", upgradeErr);
					});
				}
				if (nextSharedAccessLevel !== "preview" && (repairUrls.length || shouldHydrateOnOpen)) {
					repairSeededPlaceMedia(null, displayCapsule);
				}

				// 2) Claim/read/sender enrichment should not block showing the capsule.
				if (uid && nextSharedAccessLevel !== "preview") {
					if (shareToken) {
						setClaimStatus("claiming");
						const claimFn = httpsCallable(functions, "claimSharedCapsule");
						claimFn({ capsuleId: id, token: shareToken })
							.then(() => {
								setClaimStatus("claimed");
								setClaimError(null);
							})
							.catch((claimErr) => {
								console.error("Failed to claim shared capsule:", claimErr);
								setClaimStatus("failed");
								setClaimError(claimErr);
							});
					}
					if (!data.readBy?.includes(uid)) {
						updateDoc(ref, { readBy: arrayUnion(uid) }).catch((readErr) => {
							console.warn("Failed to mark capsule as read:", readErr);
						});
					}
					if (Array.isArray(data.unreadCommentUserIds) && data.unreadCommentUserIds.includes(uid)) {
						updateDoc(ref, { unreadCommentUserIds: arrayRemove(uid) }).catch((unreadErr) => {
							console.warn("Failed to clear unread comments:", unreadErr);
						});
					}
					recordOpenedCapsuleHistory(uid, displayCapsule).catch((historyErr) => {
						console.warn("Failed to record opened capsule history:", historyErr);
					});
				}

				// 3) Fetch sender info when available
				if (data.senderId) {
					setLoadingSender(true);
					getDoc(doc(db, "users", data.senderId))
						.then((userSnap) => {
							if (userSnap.exists()) {
								setSenderUser({ id: userSnap.id, ...userSnap.data() });
							}
						})
						.catch((senderErr) => {
							console.warn("Failed to load sender profile:", senderErr);
						})
						.finally(() => {
							setLoadingSender(false);
						});
				}
			} catch (err) {
				// An ended capsule is the scheduled outcome, not a failure to report.
				if (err?.code !== "capsule-ended") console.error(err);
				setError(err);
				setLoadPhase("error");
				setLoading(false);
			}
		};

		load();
	}, [id, uid, shareToken, repairSeededPlaceMedia]);

	useEffect(() => {
		if (!id || !capsule) return;
		// The callable requires auth, so a signed-out viewer of a public discover
		// capsule would only buy a guaranteed-failing round trip and a shimmer.
		if (!uid) return;
		if (sharedAccessLevel === "preview") return;
		if (!shouldGenerateCapsuleBody(capsule)) return;
		if (bodyGenerationStartedRef.current.has(id)) return;
		bodyGenerationStartedRef.current.add(id);

		setIsGeneratingBody(true);
		const generateFn = httpsCallable(functions, "discover_generateCapsuleBody");
		generateFn({ capsuleId: id })
			.then((result) => {
				if (!isMountedRef.current) return;
				if (result?.data?.status === "failed") {
					// Not user-facing — the template body stays on screen — but a
					// systematically broken generator should still show up in logs.
					console.warn("Capsule body generation failed:", result?.data?.reason);
					return;
				}
				const nextBody = String(result?.data?.messageBody || "").trim();
				if (result?.data?.status !== "generated" || !nextBody) return;
				setCapsule((prev) => (prev ? {
					...prev,
					messageBody: nextBody,
					messageBodyGeneratedBy: "gemini_maps_grounding",
				} : prev));
			})
			.catch((err) => {
				// The template body is already on screen; a failure is not user-facing.
				console.warn("Failed to generate capsule body:", err);
			})
			.finally(() => {
				if (isMountedRef.current) setIsGeneratingBody(false);
			});
	}, [capsule, id, uid, sharedAccessLevel]);

	const participantUserIdsKey = collectCapsuleParticipantUserIds(capsule).join("|");

	useEffect(() => {
		let cancelled = false;
		const participantUserIds = participantUserIdsKey
			? participantUserIdsKey.split("|").filter(Boolean)
			: [];

		setUsersList([]);
		if (sharedAccessLevel === "preview") return undefined;
		if (!participantUserIds.length) return undefined;

		const fetchUsers = async () => {
			try {
				const participants = await fetchUsersBasicInfoByIds(participantUserIds);
				if (!cancelled) setUsersList(participants);
			} catch (err) {
				console.error(err);
			}
		};
		fetchUsers();
		return () => {
			cancelled = true;
		};
	}, [participantUserIdsKey, sharedAccessLevel]);

	return {
		uid,
		capsule,
		senderUser,
		usersList,
		loading,
		loadingSender,
		error,
		loadPhase,
		claimStatus,
		claimError,
		sharedAccessLevel,
		repairSeededPlaceMedia,
		isGeneratingBody,
	};
}
